// ── Measured fields ─────────────────────────────────────────────────
// The planner's numeric fields hold SI values. Their markup is written in
// SI as well — a "m" / "L/min" / "bar" caption beside each input and bounds
// in those units — and declares what each field measures (`data-quantity`
// on the input, `data-field-unit` on its caption), so the fields can be
// shown, bounded and read back in the user's unit system.

import { state } from './state.ts';
import {
  getUnitLabels, toFieldText, toFieldUnits, fromFieldValue, toFieldBound,
  type FieldQuantity,
} from '../shared/units.ts';

/** Put an SI value into a numeric field, in the user's units. */
export function showField(input: HTMLInputElement, si: number, quantity: FieldQuantity, metricDigits?: number): void {
  input.value = toFieldText(si, quantity, state.userUnits, metricDigits);
}

/** Read a numeric field back in SI; NaN when it holds no number. */
export function readField(input: HTMLInputElement, quantity: FieldQuantity): number {
  return fromFieldValue(parseFloat(input.value), quantity, state.userUnits);
}

/** Caption every measured field and rescale its bounds. Run once, at boot. */
export function applyFieldUnits(root: ParentNode = document): void {
  const units = state.userUnits;
  const labels = getUnitLabels(units);
  root.querySelectorAll<HTMLElement>('[data-field-unit]').forEach((caption) => {
    caption.textContent = labels[caption.dataset.fieldUnit as FieldQuantity];
  });
  // A select of measured choices keeps SI values; only the labels convert.
  root.querySelectorAll<HTMLSelectElement>('select[data-quantity]').forEach((select) => {
    const quantity = select.dataset.quantity as FieldQuantity;
    for (const option of Array.from(select.options)) {
      const shown = Number(toFieldUnits(Number(option.value), quantity, units).toFixed(2));
      option.textContent = `${shown} ${labels[quantity]}`;
    }
  });
  if (units === 'metric') return;
  root.querySelectorAll<HTMLInputElement>('input[data-quantity]').forEach((input) => {
    const quantity = input.dataset.quantity as FieldQuantity;
    const step = Number(input.step) || 1;
    for (const bound of ['min', 'max'] as const) {
      const si = input.getAttribute(bound);
      if (si !== null) input.setAttribute(bound, String(toFieldBound(Number(si), quantity, units, step, bound)));
    }
  });
}
